import { useRouter } from 'next/router';
import { createContext, memo, type PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';

import { type Account } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useAccountContext } from '~/providers/AccountProvider';
import { FiltersModifiedManuallyProvider } from '~/providers/FiltersModifiedManuallyProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { type FilterParamsType } from '~/utils/filters/filtersToUrlParamsUtils';
import { urlQueryToFilterParams } from '~/utils/filters/urlQueryToFilterParams';

export type FiltersContextValueType = {
  /** Filters applied by a user */
  filters: Partial<FilterParamsType>;
  /** Filters specific to a page, that can't be change by a user - e.g. bookmarked on favorited assets page */
  immutableFilters: Partial<FilterParamsType> | undefined;
  /** User account id */
  accountId: Account['id'] | undefined;
  /** Search query */
  search: string | undefined;
};

const defaultValue: FiltersContextValueType = {
  filters: {},
  immutableFilters: {},
  accountId: undefined,
  search: undefined,
};

const FiltersContext = createContext<FiltersContextValueType>(defaultValue);

export type GetImmutableFilters = (params: {
  asPath: string;
  accountId: Account['id'] | undefined;
  userFilters: Pick<FiltersContextValueType, 'filters' | 'search'>;
}) => FilterParamsType;

export type FiltersProviderProps = {
  getImmutableFilters?: GetImmutableFilters;
};

export const FiltersProvider = memo(({ children, getImmutableFilters }: PropsWithChildren<FiltersProviderProps>) => {
  const { data: account } = useAccountContext();
  const { asPath, isReady } = useRouter();
  const filtersValue = useRef<FiltersContextValueType>({ ...defaultValue });

  // Stabilize getImmutableFilters to prevent unnecessary recalculations
  const stableGetImmutableFilters = useCallback(
    (params: Parameters<GetImmutableFilters>[0]) => getImmutableFilters?.(params) ?? {},
    [getImmutableFilters],
  );

  const contextValue = useMemo<FiltersContextValueType>(() => {
    // Don't process filters until router is ready (proper SSR handling)
    if (!isReady) {
      return { ...defaultValue, accountId: account?.id };
    }

    /**
     * Extract search params from asPath - this is SSR-safe and avoids unnecessary re-renders
     */
    const searchParams = asPath.includes('?') ? asPath.split('?')[1] : '';
    const query = new URLSearchParams(searchParams);
    const filters = urlQueryToFilterParams({ query });
    const immutableFilters = stableGetImmutableFilters({ asPath, accountId: account?.id, userFilters: filters });

    const newFilters = {
      ...filters,
      immutableFilters,
      accountId: account?.id,
    };

    if (isEqual(filtersValue.current, newFilters)) {
      return filtersValue.current;
    } else {
      filtersValue.current = newFilters;
      return newFilters;
    }
  }, [account?.id, asPath, isReady, stableGetImmutableFilters]);

  return (
    <FiltersContext.Provider value={contextValue}>
      <FiltersModifiedManuallyProvider>{children}</FiltersModifiedManuallyProvider>
    </FiltersContext.Provider>
  );
});

FiltersProvider.displayName = 'FiltersProvider';

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);

  if (context === defaultValue) {
    const error = 'FiltersContext used outside of FiltersProvider';
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
